// src/hooks/useHomeProducts.js
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query'
import api from '../api'

// ---- Low-level fetchers (keep response shape consistent with your API) ----
const fetchFeatured = async () => {
  const res = await api.get('/products/featured')
  return res.data?.data ?? res.data
}
const fetchNewArrivals = async () => {
  const res = await api.get('/products/new-arrivals')
  return res.data?.data ?? res.data
}
const fetchFlashSale = async () => {
  const res = await api.get('/products/flash-sale')
  return res.data?.data ?? res.data
}
const fetchBestsellers = async () => {
  const res = await api.get('/products/bestsellers')
  return res.data?.data ?? res.data
}

// ---- Individual hooks (use when you only need one list) ----
export const useFeaturedProducts = (options = {}) =>
  useQuery({
    queryKey: ['products', 'featured'],
    queryFn: fetchFeatured,
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    ...options,
  })

export const useNewArrivals = (options = {}) =>
  useQuery({
    queryKey: ['products', 'new-arrivals'],
    queryFn: fetchNewArrivals,
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    ...options,
  })

export const useFlashSaleProducts = (options = {}) =>
  useQuery({
    queryKey: ['products', 'flash-sale'],
    queryFn: fetchFlashSale,
    staleTime: 5 * 60 * 1000, // flash sales change faster
    gcTime: 60 * 60 * 1000,
    ...options,
  })

export const useBestsellers = (options = {}) =>
  useQuery({
    queryKey: ['products', 'bestsellers'],
    queryFn: fetchBestsellers,
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    ...options,
  })

// ---- All-in-one hook (parallel fetch for home/landing pages) ----
export const useHomeProducts = (options = {}) => {
  const defaults = {
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    ...options,
  }

  const results = useQueries({
    queries: [
      {
        queryKey: ['products', 'featured'],
        queryFn: fetchFeatured,
        ...defaults,
      },
      {
        queryKey: ['products', 'new-arrivals'],
        queryFn: fetchNewArrivals,
        ...defaults,
      },
      {
        queryKey: ['products', 'flash-sale'],
        queryFn: fetchFlashSale,
        // override: flash sale is more volatile
        staleTime: 5 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
        ...options?.flashSale, // allow fine-grained overrides
      },
      {
        queryKey: ['products', 'bestsellers'],
        queryFn: fetchBestsellers,
        ...defaults,
      },
    ],
  })

  const [featured, newArrivals, flashSale, bestsellers] = results

  const isLoading =
    featured.isLoading ||
    newArrivals.isLoading ||
    flashSale.isLoading ||
    bestsellers.isLoading

  const isError =
    featured.isError ||
    newArrivals.isError ||
    flashSale.isError ||
    bestsellers.isError

  return {
    featured,
    newArrivals,
    flashSale,
    bestsellers,
    isLoading,
    isError,
  }
}
